import { Platform } from 'react-native';

export const tokens = {
  breakpoints: {
    mobile: 0,
    tablet: 780,
    desktop: 1080,
    wide: 1520,
  },

  spacing: {
    none: 0,
    xxs: 2,
    xs: 4,
    sm: 8,
    smPlus: 10,
    md: 12,
    mdPlus: 14,
    lg: 16,
    lgPlus: 20,
    xl: 24,
    '2xl': 32,
    xxl: 32,
    xxlPlus: 40,
    '3xl': 48,
    xxxl: 48,
    '4xl': 64,
    huge: 64,
  },

  radius: {
    none: 0,
    sm: 6,
    smPlus: 8,
    md: 10,
    mdPlus: 12,
    lg: 14,
    xl: 18,
    xlPlus: 20,
    '2xl': 24,
    '3xl': 30,
    full: 9999,
  },

  fontSize: {
    xs: 11,
    sm: 12,
    smPlus: 13,
    base: 14,
    basePlus: 15,
    md: 16,
    lg: 18,
    xl: 20,
    xlPlus: 22,
    '2xl': 24,
    '3xl': 30,
    '4xl': 36,
    '5xl': 48,
  },

  lineHeight: {
    tight: 1.1,
    snug: 1.2,
    normal: 1.5,
    relaxed: 1.75,
    loose: 2,
  },

  fontWeight: {
    light: '300' as const,
    normal: '400' as const,
    medium: '500' as const,
    semibold: '600' as const,
    bold: '700' as const,
    extrabold: '800' as const,
    black: '900' as const,
  },

  letterSpacing: {
    tighter: -0.8,
    tight: -0.4,
    normal: 0,
    wide: 0.4,
    wider: 0.8,
    widest: 1.6,
  },

  duration: {
    instant: 90,
    fast: 170,
    normal: 280,
    slow: 420,
    entrance: 620,
  },

  easing: {
    default: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    spring: 'cubic-bezier(0.22, 1, 0.36, 1)',
    entrance: 'cubic-bezier(0.16, 1, 0.3, 1)',
    exit: 'cubic-bezier(0.4, 0, 1, 1)',
  },

  interaction: {
    minimumTarget: {
      default: 44,
      android: 48,
    },
    focusRing: {
      width: 2,
      offset: 2,
    },
  },

  // Backdrop blur, web only. Chrome (top bar, sidebar) is frosted rather than
  // opaque so the canvas keeps reading through it; cards use the smaller
  // blur baked into surface.bento.
  blur: {
    chrome: 18,
  },

  zIndex: {
    base: 0,
    dropdown: 100,
    sticky: 200,
    modal: 300,
    popover: 400,
    toast: 500,
    tooltip: 600,
  },

  shadow: Platform.select({
    ios: {
      xs: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.06,
        shadowRadius: 3,
      },
      subtle: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
      },
      sm: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
      },
      md: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.16,
        shadowRadius: 16,
      },
      lg: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.22,
        shadowRadius: 24,
      },
      xl: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 16 },
        shadowOpacity: 0.26,
        shadowRadius: 32,
      },
    },
    android: {
      xs: { elevation: 1 },
      subtle: { elevation: 3 },
      sm: { elevation: 4 },
      md: { elevation: 8 },
      lg: { elevation: 14 },
      xl: { elevation: 20 },
    },
    web: {
      xs: { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.14)' },
      subtle: { boxShadow: '0 10px 30px rgba(0, 0, 0, 0.16)' },
      sm: { boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2)' },
      md: { boxShadow: '0 18px 36px rgba(0, 0, 0, 0.24)' },
      lg: { boxShadow: '0 24px 48px rgba(0, 0, 0, 0.28)' },
      xl: { boxShadow: '0 34px 70px rgba(0, 0, 0, 0.34)' },
    },
  }),
} as const;

export type Tokens = typeof tokens;
export type SpacingKey = keyof typeof tokens.spacing;
export type BreakpointKey = keyof typeof tokens.breakpoints;
export type FontSizeKey = keyof typeof tokens.fontSize;
export type FontWeightKey = keyof typeof tokens.fontWeight;
export type RadiusKey = keyof typeof tokens.radius;
export type ShadowKey = 'xs' | 'subtle' | 'sm' | 'md' | 'lg' | 'xl';
